import { Alert, Box, Button, Skeleton, Stack, Typography } from "@mui/material";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { useCheckPermission } from "Hooks/usePermission";
import { usePartyActingPersonParty } from "Hooks/useUserActingPersonParty";
import {
  type IdentityVerificationStatus,
  type PartyDto,
  PartyType,
  Permission,
} from "Infrastructure/Api/Api";
import { AccessRightCodes } from "Models/AccessRightCodes";
import { Resources, useResource } from "Translations/Resources";
import { AddressesSection } from "Components/Users/UserDetailPageContent/Components/PartyReadOnlyDetail/AddressesSection";
import { DocumentsSection } from "Components/Users/UserDetailPageContent/Components/PartyReadOnlyDetail/DocumentsSection";
import { IdentityVerificationButton } from "Components/Users/UserDetailPageContent/Components/PartyReadOnlyDetail/IdentityVerificationButton";
import { LegalEntitySection } from "Components/Users/UserDetailPageContent/Components/PartyReadOnlyDetail/LegalEntitySection";
import { NaturalPersonSection } from "Components/Users/UserDetailPageContent/Components/PartyReadOnlyDetail/NaturalPersonSection";
import { SelfEmployedSection } from "Components/Users/UserDetailPageContent/Components/PartyReadOnlyDetail/SelfEmployedSection";

const PARTY_RESOURCES =
  Resources.Pages.Content.UserDetailPageContent.Tabs.Party;
const FORM_RESOURCES = PARTY_RESOURCES.Form;

type Props = {
  party: PartyDto;
  onEdit?: () => void;
  userID: number;
  currentVerificationStatus: IdentityVerificationStatus;
  isUboUnknown?: boolean;
};

export const PartyReadOnlyDetail = ({
  party,
  onEdit,
  userID,
  currentVerificationStatus,
  isUboUnknown,
}: Props) => {
  const { t } = useResource();
  const canSeeDocuments = useCheckPermission(
    AccessRightCodes.IDDocuments,
    Permission.Read,
  );
  const canUpdateIDVerification = useCheckPermission(
    AccessRightCodes.IDVerifications,
    Permission.Update,
  );

  const isLegalEntity = party.type === PartyType.LegalEntity;
  const { actingPartyData, isLoading: isActingPersonLoading } =
    usePartyActingPersonParty(
      isLegalEntity ? (party.publicID ?? undefined) : undefined,
    );
  const docsParty = isLegalEntity ? actingPartyData : party;

  const genderValue = t(Resources.Form.PersonalData.Sex[party.gender]);

  const typeResources =
    party.type === PartyType.LegalEntity
      ? FORM_RESOURCES.LegalEntity
      : party.type === PartyType.SelfEmployed
        ? FORM_RESOURCES.SelfEmployed
        : FORM_RESOURCES.NaturalPerson;

  return (
    <PrimaryPaper>
      <Box
        display="flex"
        flexWrap="wrap-reverse"
        justifyContent="space-between"
        alignItems="flex-start"
        gap={1}
        mb={3}
      >
        <Box>
          <Typography variant="body2" color="text.secondary" mb={0.5}>
            {t(typeResources.Type)}
          </Typography>
          <Typography>{t(Resources.Form.PartyType[party.type])}</Typography>
        </Box>
        <Box display="flex" gap={1} flexShrink={0}>
          {canUpdateIDVerification && (
            <IdentityVerificationButton
              userID={userID}
              currentStatus={currentVerificationStatus}
              isDisabled={
                party.type !== PartyType.LegalEntity &&
                !party.hasIdentificationsUploaded
              }
              disabledTooltip={t(PARTY_RESOURCES.NoDocumentsUploaded)}
            />
          )}
          {onEdit && (
            <Button variant="contained" size="small" onClick={onEdit}>
              {t(Resources.Common.Edit)}
            </Button>
          )}
        </Box>
      </Box>

      <Stack gap={4}>
        {party.type === PartyType.NaturalPerson && (
          <NaturalPersonSection party={party} genderValue={genderValue} />
        )}

        {party.type === PartyType.SelfEmployed && (
          <SelfEmployedSection party={party} genderValue={genderValue} />
        )}

        {party.type === PartyType.LegalEntity && (
          <LegalEntitySection party={party} />
        )}

        {isUboUnknown && party.type === PartyType.LegalEntity && (
          <Alert severity="info">{t(Resources.AML.UboUnknownAlert)}</Alert>
        )}

        <AddressesSection party={party} />

        {canSeeDocuments && isLegalEntity && isActingPersonLoading && (
          <Skeleton height={300} variant="rounded" />
        )}
        {canSeeDocuments &&
          isLegalEntity &&
          !isActingPersonLoading &&
          !docsParty && (
            <Alert severity="info">
              {t(PARTY_RESOURCES.NoActingPersonLinked)}
            </Alert>
          )}
        {canSeeDocuments && docsParty && !isActingPersonLoading && (
          <DocumentsSection
            party={docsParty}
            currentVerificationStatus={currentVerificationStatus}
          />
        )}
      </Stack>
    </PrimaryPaper>
  );
};
